import { DateRange, isoDateLocal } from './aggregate';
import type { MetaFila, MetaSerieDia } from '../types/indicadores.types';

/**
 * Utilidades PURAS del tablero de Presupuesto. Todo lo que decide qué ve
 * dirección vive aquí y se prueba sin render: el rollup, el color del semáforo
 * y el acumulado de la curva.
 */

/** Niveles del segmentado de la tabla. El rollup es client-side sobre las mismas filas. */
export type NivelMeta = 'regional' | 'supervisor' | 'tienda';

export interface FilaRollup {
  clave: string;
  etiqueta: string;
  /** Sub-etiqueta: para tienda, su cadena; para supervisor, su regional. */
  detalle: string | null;
  metaMonto: number;
  metaPares: number;
  ventaMonto: number;
  ventaPares: number;
  faltanteMonto: number;
  cumplimientoMonto: number | null;
  cumplimientoPares: number | null;
  tiendas: number;
  /** Tiendas del grupo con meta cargada. Menos que `tiendas` = denominador incompleto. */
  tiendasConMeta: number;
}

/**
 * Agrupa las filas por nivel.
 *
 * ⚠️ El cumplimiento del grupo se calcula sobre los TOTALES del grupo, nunca
 * promediando los porcentajes de las tiendas. Un promedio de porcentajes le da
 * el mismo peso a una tienda de $30.000 que a una de $3.000.000 y saca un
 * número que no es el de nadie.
 *
 * Y sólo suman al numerador y al denominador las tiendas CON meta: incluir la
 * venta de una tienda sin meta inflaría un porcentaje cuyo denominador no la
 * cubre.
 */
export function rollupMeta(filas: MetaFila[], nivel: NivelMeta): FilaRollup[] {
  const grupos = new Map<string, FilaRollup>();

  for (const f of filas) {
    const { clave, etiqueta, detalle } = claveDe(f, nivel);
    let g = grupos.get(clave);
    if (!g) {
      g = {
        clave,
        etiqueta,
        detalle,
        metaMonto: 0,
        metaPares: 0,
        ventaMonto: 0,
        ventaPares: 0,
        faltanteMonto: 0,
        cumplimientoMonto: null,
        cumplimientoPares: null,
        tiendas: 0,
        tiendasConMeta: 0,
      };
      grupos.set(clave, g);
    }
    g.tiendas += 1;
    if (!f.conMeta) continue;
    g.tiendasConMeta += 1;
    g.metaMonto += f.metaMonto;
    g.metaPares += f.metaPares;
    g.ventaMonto += f.ventaMonto;
    g.ventaPares += f.ventaPares;
  }

  const salida = Array.from(grupos.values());
  for (const g of salida) {
    g.cumplimientoMonto = g.metaMonto > 0 ? g.ventaMonto / g.metaMonto : null;
    g.cumplimientoPares = g.metaPares > 0 ? g.ventaPares / g.metaPares : null;
    g.faltanteMonto = Math.max(0, g.metaMonto - g.ventaMonto);
  }

  return salida.sort((a, b) => b.ventaMonto - a.ventaMonto);
}

function claveDe(
  f: MetaFila,
  nivel: NivelMeta,
): { clave: string; etiqueta: string; detalle: string | null } {
  if (nivel === 'regional') {
    const r = f.regional || '(sin regional)';
    return { clave: r, etiqueta: r, detalle: null };
  }
  if (nivel === 'supervisor') {
    const s = f.supervisor || '(sin supervisor)';
    return { clave: s, etiqueta: s, detalle: f.regional || null };
  }
  return {
    clave: `${f.chainId}|${f.branchOfficeId}`,
    etiqueta: f.nombre || `${f.chainId} ${f.branchOfficeId}`,
    detalle: f.chainId,
  };
}

/**
 * Semáforo del cumplimiento, en fracción (1 = meta cumplida).
 *
 * ⚠️ NO reutilizar `tasaColor`: ese tiene la semántica INVERTIDA a propósito
 * (alto = malo, porque mide venta fuera del sistema). Aquí alto es bueno.
 * Umbrales: >=1 verde, >=0,9 dorado, por debajo rojo.
 */
export function metaColor(fraccion: number | null | undefined): string {
  if (fraccion === null || fraccion === undefined || Number.isNaN(fraccion)) {
    return '#9298a3';
  }
  if (fraccion >= 1) return '#17a44d';
  if (fraccion >= 0.9) return '#b7891f';
  return '#d63a33';
}

/** Rango del día de hoy. */
export function todayRange(today: Date = new Date()): DateRange {
  const iso = isoDateLocal(today);
  return { desde: iso, hasta: iso };
}

/**
 * Mes EN CURSO, del día 1 al último del mes.
 *
 * La meta es un instrumento mensual: `previousMonthRange` sirve para mirar
 * atrás, pero no responde «¿cuánto me falta?», que es la pregunta de la vista.
 * El rango llega hasta el fin de mes a propósito, aunque sea futuro: el
 * denominador del mes es la meta del mes completo, y `cmin_met` ya trae los
 * días por venir cargados.
 */
export function currentMonthRange(today: Date = new Date()): DateRange {
  const y = today.getFullYear();
  const m = today.getMonth();
  return {
    desde: isoDateLocal(new Date(y, m, 1)),
    hasta: isoDateLocal(new Date(y, m + 1, 0)),
  };
}

export interface PuntoAcumulado {
  fecha: string;
  /** Meta acumulada hasta ese día. Siempre hay, incluso en días futuros. */
  meta: number;
  /**
   * Venta acumulada hasta ese día, o `null` a partir del primer día sin dato.
   * La curva se CORTA ahí en vez de seguir plana: una línea horizontal en el
   * futuro se lee como «dejaron de vender», que es justo lo contrario.
   */
  venta: number | null;
}

/**
 * Acumula la serie diaria para la curva.
 *
 * En cuanto un día no tiene venta materializada, todos los siguientes vuelven
 * `null`. Arrastrar el último acumulado dibujaría una meseta que nadie vivió.
 */
export function acumular(
  serie: MetaSerieDia[],
  campo: 'monto' | 'pares' = 'monto',
): PuntoAcumulado[] {
  const meta = campo === 'monto' ? 'metaMonto' : 'metaPares';
  const venta = campo === 'monto' ? 'ventaMonto' : 'ventaPares';
  let accMeta = 0;
  let accVenta = 0;
  let cortada = false;

  return serie.map((d) => {
    accMeta += d[meta] ?? 0;
    if (d[venta] === null || d[venta] === undefined) cortada = true;
    if (!cortada) accVenta += d[venta] as number;
    return { fecha: d.fecha, meta: accMeta, venta: cortada ? null : accVenta };
  });
}

/** `YYYY-MM-DD` -> `DD/MM`, igual que el resto del módulo. */
export function diaCorto(fecha: string): string {
  const [, mm, dd] = fecha.split('-');
  return `${dd}/${mm}`;
}

export interface TotalesMeta {
  metaMonto: number;
  metaPares: number;
  ventaMonto: number;
  ventaPares: number;
  cumplimientoMonto: number | null;
  cumplimientoPares: number | null;
  faltanteMonto: number;
  faltantePares: number;
  tiendasConMeta: number;
  tiendasSinMeta: number;
  ventaSinMeta: number;
}

/**
 * Totales del ámbito a partir de las filas.
 *
 * POR QUÉ EXISTE, si la API ya manda `totales`: los filtros de Regional,
 * Supervisor y multi-tienda son CLIENT-SIDE (el endpoint sólo acepta una cadena
 * y una sucursal), así que en cuanto se acota por regional hay que recalcular.
 * Tener dos caminos —a veces los totales de la API, a veces los míos— es cómo
 * la cabecera acaba diciendo una cosa y la tabla otra.
 *
 * Así que se calcula SIEMPRE aquí, y una prueba fija que sobre el ámbito
 * completo da exactamente lo que manda la API.
 */
export function totalizarMeta(filas: MetaFila[]): TotalesMeta {
  const conMeta = filas.filter((f) => f.conMeta);
  const sinMeta = filas.filter((f) => !f.conMeta);
  const sum = (xs: MetaFila[], k: keyof MetaFila) =>
    xs.reduce((a, x) => a + (x[k] as number), 0);

  const metaMonto = sum(conMeta, 'metaMonto');
  const metaPares = sum(conMeta, 'metaPares');
  const ventaMonto = sum(conMeta, 'ventaMonto');
  const ventaPares = sum(conMeta, 'ventaPares');

  return {
    metaMonto,
    metaPares,
    ventaMonto,
    ventaPares,
    cumplimientoMonto: metaMonto > 0 ? ventaMonto / metaMonto : null,
    cumplimientoPares: metaPares > 0 ? ventaPares / metaPares : null,
    faltanteMonto: Math.max(0, metaMonto - ventaMonto),
    faltantePares: Math.max(0, metaPares - ventaPares),
    tiendasConMeta: conMeta.length,
    tiendasSinMeta: sinMeta.length,
    ventaSinMeta: sum(sinMeta, 'ventaMonto'),
  };
}
